import PropTypes from 'prop-types';
import { useState, useRef, useMemo, useEffect } from 'react';
import { IconButton, Menu, MenuItem, Box, CircularProgress, Button, Paper, Tooltip, Typography, TextField } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { postTicketText } from '../../api/tickets';
import Markdown from '../markdown/Markdown';
import { handleSetVerticalPosition } from './handlePosition';

// ----------------------------------------------------------------------

AIAssistButton.propTypes = {
  value: PropTypes.string.isRequired,
  quillRef: PropTypes.object,
  visible: PropTypes.bool,
  sx: PropTypes.object,
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      mode: PropTypes.string.isRequired,
    })
  ),
};

export default function AIAssistButton({
  value,
  quillRef,
  visible = true,
  sx = {},
  menuOptions = [
    { label: 'Rephrase', mode: 'rephrase' },
    { label: 'Shorten', mode: 'shorten' },
    { label: 'Enhance', mode: 'enhance' },
    { label: 'Custom...', mode: 'custom' },
  ],
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [aiText, setAiText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');
  const [cursorVerticalPosition, setCursorVerticalPosition] = useState(0);
  const [capturedSelection, setCapturedSelection] = useState(null);
  const [capturedRange, setCapturedRange] = useState(null);
  const [hasError, setHasError] = useState(false);

  const abortControllerRef = useRef(null);

  const imageMapRef = useRef([]);
  const attachmentMapRef = useRef([]);

  const isValueEmpty = useMemo(() => {
    if (!value) return true;
    
    const strippedValue = value.replace(/<[^>]*>/g, '').trim();
    
    if (!strippedValue) return true;
    
    const defaultSignaturePattern = /^(Thanks,\s*[\w\s]+)?$/i;
    return defaultSignaturePattern.test(strippedValue);
  }, [value]);

  const handleMenuClose = () => {

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    imageMapRef.current = [];
    attachmentMapRef.current = [];

    setAnchorEl(null);
    setAiText('');
    setShowSuggestion(false);
    setShowCustomInput(false);
    setCustomInstruction('');
    setIsGenerating(false);
    setCapturedSelection(null);
    setCapturedRange(null);
  };

  useEffect(() => {
    if (!quillRef?.current || !visible) return undefined;

    const quill = quillRef.current.getEditor();
    if (!quill) return undefined;

    const handleSelectionChange = (range) => {
      const position = handleSetVerticalPosition(quill, range);
      setCursorVerticalPosition(position);
    };

    quill.on('selection-change', handleSelectionChange);

    return () => {
      quill.off('selection-change', handleSelectionChange);
    };
  }, [quillRef, visible]);

  useEffect(() => {
    if (!visible) {
      handleMenuClose();
    }
  }, [visible]);

  if (!visible) {
    return null;
  }

  const getTextSuggestion = async (mode, instruction) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsGenerating(true);
    setAiText('Generating...');
    setShowSuggestion(true);
    setHasError(false);
    
    let textToProcess = value;

    if (capturedSelection) {
      textToProcess = capturedSelection;
    } else {
      const quill = quillRef?.current?.getEditor();
      const range = quill?.getSelection();
      if (range && range.length > 0) {
        textToProcess = quill.getText(range.index, range.length);
      }
    }

    let imageIndex = 0;
    imageMapRef.current = [];

    let cleanedText = textToProcess.replace(/<img[^>]*>/gi, (match) => {
      const placeholder = `[[IMAGE_${imageIndex}]]`;
      imageMapRef.current.push({ placeholder, original: match });
      imageIndex++;
      return placeholder;
    });

    let attachmentIndex = 0;
    attachmentMapRef.current = [];

    cleanedText = cleanedText.replace(
      /<a[^>]*class="attachment"[^>]*>.*?<\/a>/gi,
      (match) => {
        const placeholder = `[[ATTACHMENT_${attachmentIndex}]]`;
        attachmentMapRef.current.push({ placeholder, original: match });
        attachmentIndex++;
        return placeholder;
      }
    );
    try {
      const response = await postTicketText(
        cleanedText,
        mode,
        instruction,
        abortControllerRef.current.signal
      );
      if (!response) {
        setAiText('Error getting suggestion');
        setHasError(true);
      }
      else {
        setAiText(response.result);
        setHasError(!response.success);
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        setAiText('Error getting suggestion');
        setHasError(true);
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
    }
  };

  const parsePlaceholders = (text) => {
    const regex = /(\[\[(?:IMAGE|ATTACHMENT)_\d+\]\])/g;

    return text.split(regex).filter(Boolean).map((part) => {
      if (/\[\[IMAGE_\d+\]\]/.test(part)) {
        return { type: 'image', value: part };
      }
      if (/\[\[ATTACHMENT_\d+\]\]/.test(part)) {
        return { type: 'attachment', value: part };
      }
      return { type: 'text', value: part };
    });
  };

  const renderAiText = () => {
    const tokens = parsePlaceholders(aiText);

    return tokens.map((token, index) => {
      if (token.type === 'image') {
        return (
          <Tooltip key={index} title="Image">
            <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', mx: 0.5 }}>
              <ImageIcon />
            </Box>
          </Tooltip>
        );
      }

      if (token.type === 'attachment') {
        return (
          <Tooltip key={index} title="Attachment placeholder">
            <TextSnippetIcon sx={{ verticalAlign: 'middle', mx: 0.5 }} />
          </Tooltip>
        );
      }

      return <Markdown key={index}>{token.value}</Markdown>;
    });
  };

  const handleReplace = () => {
    if (!quillRef?.current) return;

    const quill = quillRef.current.getEditor();

    let finalText = aiText;

    imageMapRef.current.forEach(({ placeholder, original }) => {
      finalText = finalText.replaceAll(placeholder, original);
    });

    attachmentMapRef.current.forEach(({ placeholder, original }) => {
      finalText = finalText.replaceAll(placeholder, original);
    });

    const rangeToUse = capturedRange || quill.getSelection();

    if (rangeToUse && rangeToUse.length > 0) {
      const originalText = quill.getText(rangeToUse.index, rangeToUse.length);
      const hadTrailingNewline = originalText.endsWith('\n');

      quill.deleteText(rangeToUse.index, rangeToUse.length, 'user');

      let textToInsert = finalText;
      if (hadTrailingNewline && !finalText.endsWith('\n')) {
        textToInsert += '\n';
      }

      quill.insertText(rangeToUse.index, textToInsert, 'user');
      quill.setSelection(rangeToUse.index + textToInsert.length, 0);
    } else {
      const delta = quill.clipboard.convert(finalText);
      quill.setContents(delta, 'user');
      const length = quill.getLength();
      quill.setSelection(length - 1, 0);
    }

    handleMenuClose();
  };

  const handleAIClick = (event) => {
    const quill = quillRef?.current?.getEditor();
    let selectedText = null;
    let selectionRange = null;

    const range = quill?.getSelection();
    if (range && range.length > 0) {
      selectedText = quill.getText(range.index, range.length);
      selectionRange = { index: range.index, length: range.length };
    }

    setCapturedSelection(selectedText);
    setCapturedRange(selectionRange);
    setAnchorEl(event.currentTarget);
    setAiText('');
    setShowSuggestion(false);
    setShowCustomInput(false);
    setCustomInstruction('');
  };
  const handleModeSelect = (mode) => {
    if (mode === 'custom') {
      setShowCustomInput(true);
      return;
    }
    getTextSuggestion(mode, null);

  };
  const handleCustomSubmit = () => {
    if (!customInstruction.trim()) return;
    setShowCustomInput(false);
    getTextSuggestion('custom', customInstruction);
  };
  const handleCustomCancel = () => {
    setShowCustomInput(false);
    setCustomInstruction('');
  };
  return (
    <>
      <Tooltip title="Bob AI Assistant" placement="top">
        <IconButton
          sx={{
            position: 'absolute',
            top: `${cursorVerticalPosition}px`,
            left: 0,
            zIndex: 2,
            filter: !anchorEl ? 'grayscale(100%)' : 'grayscale(0%)',
            transition: 'filter 0.3s ease-in-out',
            pr: 0,
            mr: 0,
            ...sx,
          }}
          onClick={handleAIClick}
        >
          <img src="/assets/bobAI/bobAIlogo_2.png" alt="AI" width={20} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        autoFocus={false}
        disableAutoFocusItem
        disableRestoreFocus
        PaperProps={{
          sx: {
            minWidth: 320,
            maxWidth: 500,
            border: '1px solid',
            borderColor: '#c1c9d0',
          },
        }}
        MenuListProps={{ 
          sx: { p: 0 },
          autoFocusItem: false,
          disabledItemsFocusable: false
        }}
      >
        {!showSuggestion && !showCustomInput && (
          <Box>
            {isValueEmpty ? (
              <Box sx={{ p: 2, maxWidth: 400 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center' }}
                >
                  BobAI can rewrite and adjust your messages to make
                  them even better. Type a message to get started.
                </Typography>
              </Box>
            ) : (
              menuOptions.map((option) => (
                <MenuItem
                  key={option.mode}
                  onClick={() => handleModeSelect(option.mode)}
                >
                  {option.label}
                </MenuItem>
              ))
            )}
          </Box>
        )}
        {showCustomInput && (
          <Box sx={{ p: 2, minWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Enter your custom instruction
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              autoFocus
              value={customInstruction}
              placeholder="Example: Make it more professional"
              onChange={(e) => setCustomInstruction(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleCustomSubmit();
                }
              }}
            />
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                mt: 2
              }}
            >
              <Button
                onClick={handleCustomCancel}
                size="small"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCustomSubmit}
                variant="contained"
                size="small"
                disabled={!customInstruction.trim()}
              >
                Generate
              </Button>
            </Box>
          </Box>
        )}
        {showSuggestion && (
          <Box sx={{ p: 2 }}>
            {isGenerating ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 2
                }}
              >
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography>Generating...</Typography>
              </Box>
            ) : (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: 'grey.200',
                    maxHeight: 300,
                    overflow: 'auto'
                  }}
                >
                  {renderAiText()}
                </Paper>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Button
                    onClick={handleMenuClose}
                    size="small"
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleReplace} 
                    variant="contained"
                    size="small"
                    disabled={
                      isGenerating ||
                      hasError ||
                      !aiText ||
                      aiText.trim() === ''
                    }>
                    Replace
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Menu>
    </>
  );
}